/* eslint-disable no-undef */
import React, { useState, useEffect, useCallback } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../context/LanguageContext';
import { USDT_ADDRESS, NFT_CONTRACT_ADDRESS, USDT_ABI, NFT_ABI } from '../config/contracts';
import { toast } from 'react-toastify';
import './MintingSection.css';

const BSC_RPC_URLS = [
  'https://bsc-dataseed.bnbchain.org',
  'https://bsc-dataseed1.bnbchain.org',
  'https://bsc-dataseed2.bnbchain.org',
  'https://bsc-dataseed3.bnbchain.org'
];
const BSC_CHAIN_ID = 56;

const MintingSection = () => {
  const { web3, account, isConnected } = useWeb3();
  const { t } = useLanguage();
  const [minting, setMinting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [presaleMinted, setPresaleMinted] = useState(0);
  const [maxPresale, setMaxPresale] = useState(0);
  const [mintPrice, setMintPrice] = useState('900');
  // eslint-disable-next-line no-unused-vars
  const [isPresale, setIsPresale] = useState(true);
  const [usdtApproved, setUsdtApproved] = useState(false);
  const [alreadyOwnsNft, setAlreadyOwnsNft] = useState(false);

  const getReadOnlyWeb3 = useCallback(async () => {
    try {
      const { Web3 } = await import('web3');
      return new Web3(BSC_RPC_URLS[0]);
    } catch (_) {
      return null;
    }
  }, []);

  const getErrorMessage = (error) => {
    if (!error) return 'Unknown error';
    if (typeof error === 'string') return error;
    if (error?.message && typeof error.message === 'string') return error.message;
    if (error?.reason && typeof error.reason === 'string') return error.reason;
    if (error?.data?.message && typeof error.data.message === 'string') return error.data.message;
    if (error?.error?.message && typeof error.error.message === 'string') return error.error.message;
    try {
      return JSON.stringify(error);
    } catch (_) {
      return String(error);
    }
  };

  // Check if USDT is approved for the NFT contract
  const checkUSDTApproval = useCallback(async () => {
    if (!web3 || !account) {
      setUsdtApproved(false);
      return false;
    }

    if (!NFT_CONTRACT_ADDRESS || NFT_CONTRACT_ADDRESS.trim() === '') {
      setUsdtApproved(false);
      return false;
    }

    // If wallet already owns an NFT, don't block UI on approval state.
    if (alreadyOwnsNft) {
      setUsdtApproved(true);
      return true;
    }

    try {
      const roWeb3 = await getReadOnlyWeb3();
      if (!roWeb3) return false;
      const price = roWeb3.utils.toWei(mintPrice.toString(), 'ether');
      const roUsdtContract = new roWeb3.eth.Contract(USDT_ABI, USDT_ADDRESS);
      const allowance = await roUsdtContract.methods.allowance(account, NFT_CONTRACT_ADDRESS).call();
      
      // Web3.js v4 doesn't have toBN, use BigInt instead
      const allowanceBigInt = BigInt(allowance.toString());
      const priceBigInt = BigInt(price.toString());
      const isApproved = allowanceBigInt >= priceBigInt;
      
      setUsdtApproved(isApproved);
      
      if (isApproved) {
        console.log('USDT approval check: Approved', { 
          allowance: allowance.toString(), 
          allowanceBigInt: allowanceBigInt.toString(),
          price: price.toString(),
          priceBigInt: priceBigInt.toString()
        });
      } else {
        console.log('USDT approval check: Not approved', { 
          allowance: allowance.toString(), 
          allowanceBigInt: allowanceBigInt.toString(),
          price: price.toString(),
          priceBigInt: priceBigInt.toString()
        });
      }
      
      return isApproved;
    } catch (error) {
      console.error('Error checking USDT approval:', error);
      // Keep previous state on RPC/provider errors to avoid locking mint UI.
      return false;
    }
  }, [web3, account, mintPrice, alreadyOwnsNft, getReadOnlyWeb3]);

  useEffect(() => {
    // Fetch presale statistics from contract
    const fetchPresaleStats = async () => {
      if (!NFT_CONTRACT_ADDRESS) {
        setMaxPresale(100);
        setPresaleMinted(0);
        setMintPrice('900');
        setAlreadyOwnsNft(false);
        return;
      }

      try {
        const web3Instance = await getReadOnlyWeb3();
        if (!web3Instance) return;
        const nftContract = new web3Instance.eth.Contract(NFT_ABI, NFT_CONTRACT_ADDRESS);
        const safeCall = async (fn, fallback = null) => { // eslint-disable-line no-unused-vars
          try {
            return await fn();
          } catch (_) {
            return fallback;
          }
        };
        const callMethod = async (methodNames, args = [], fallback = null) => {
          for (const name of methodNames) {
            try {
              if (typeof nftContract?.methods?.[name] !== 'function') continue;
              return await nftContract.methods[name](...args).call();
            } catch (_) {
              // try next candidate
            }
          }
          return fallback;
        };

        // Fetch each field safely so one failing call does not blank all stats.
        const [
          presaleMintedCount,
          totalSupplyCount,
          presaleSupply,
          presaleActive,
          publicActive,
          presalePrice,
          publicPrice,
          walletBalance
        ] = await Promise.all([
          callMethod(['presaleMinted', 'presaleMintedCount'], [], null),
          callMethod(['totalSupply'], [], '0'),
          callMethod(['PRESALE_SUPPLY', 'presaleSupply'], [], '100'),
          callMethod(['presaleActive', 'isPresaleActive'], [], true),
          callMethod(['publicSaleActive', 'publicActive', 'isPublicSaleActive'], [], false),
          callMethod(['PRESALE_PRICE', 'presalePrice'], [], null),
          callMethod(['PUBLIC_PRICE', 'publicPrice'], [], null),
          account ? callMethod(['balanceOf'], [account], '0') : Promise.resolve('0')
        ]);

        const mintedFromPresale = Number.parseInt(String(presaleMintedCount ?? '0'), 10);
        const mintedFromTotal = Number.parseInt(String(totalSupplyCount ?? '0'), 10);
        const resolvedMinted = Number.isFinite(mintedFromPresale) && mintedFromPresale > 0
          ? mintedFromPresale
          : (Number.isFinite(mintedFromTotal) ? mintedFromTotal : 0);
        const resolvedSupply = Number.parseInt(String(presaleSupply ?? '100'), 10);
        const hasNft = Boolean(account) && Number.parseInt(String(walletBalance || '0'), 10) > 0;

        setPresaleMinted(Number.isFinite(resolvedMinted) ? resolvedMinted : 0);
        setMaxPresale(Number.isFinite(resolvedSupply) ? resolvedSupply : 100);
        setIsPresale(Boolean(presaleActive) && !Boolean(publicActive));
        setAlreadyOwnsNft(hasNft);

        // Use active phase price; fall back safely if one read fails.
        const activePriceWei = Boolean(publicActive) ? (publicPrice || presalePrice) : (presalePrice || publicPrice);
        if (activePriceWei) {
          const priceInUSDT = web3Instance.utils.fromWei(activePriceWei, 'ether');
          setMintPrice(parseFloat(priceInUSDT).toString());
        }
      } catch (error) {
        console.error('Error fetching presale stats:', error);
        // Keep the last good values instead of forcing placeholders.
      }
    };

    fetchPresaleStats();
    
    // Check USDT approval if wallet is connected
    if (web3 && account && NFT_CONTRACT_ADDRESS && !alreadyOwnsNft) {
      checkUSDTApproval();
    }
  }, [web3, account, checkUSDTApproval, alreadyOwnsNft, getReadOnlyWeb3]);

  useEffect(() => {
    if (web3 && account && mintPrice && NFT_CONTRACT_ADDRESS && !alreadyOwnsNft) {
      checkUSDTApproval();
    }
  }, [web3, account, mintPrice, checkUSDTApproval, alreadyOwnsNft]);

  // Periodically check approval status when wallet is connected
  useEffect(() => {
    if (!web3 || !account || !NFT_CONTRACT_ADDRESS || alreadyOwnsNft) return;

    const interval = setInterval(() => {
      checkUSDTApproval();
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [web3, account, mintPrice, checkUSDTApproval, alreadyOwnsNft]);

  // Approve USDT spending
  const handleApproveUSDT = async () => {
    // Check wallet connection first
    if (!isConnected || !account) {
      toast.error(t('pleaseConnectWallet'));
      return;
    }

    // Check web3 provider
    if (!web3) {
      toast.error('Web3 provider not available. Please refresh the page.');
      return;
    }

    // Check NFT contract address
    if (!NFT_CONTRACT_ADDRESS || NFT_CONTRACT_ADDRESS.trim() === '') {
      toast.error(t('contractNotDeployed'));
      console.error('NFT_CONTRACT_ADDRESS is not set. Please configure it in your .env file.');
      return;
    }

    // Validate contract address format
    if (!web3.utils.isAddress(NFT_CONTRACT_ADDRESS)) {
      toast.error('Invalid NFT contract address');
      return;
    }

    setApproving(true);
    try {
      const chainId = Number(await web3.eth.getChainId());
      if (chainId !== BSC_CHAIN_ID) {
        toast.error('Please switch wallet network to BNB Smart Chain.');
        return;
      }
      const usdtContract = new web3.eth.Contract(USDT_ABI, USDT_ADDRESS);
      
      // Approve a large amount to avoid multiple approvals
      const maxApproval = web3.utils.toWei('10000', 'ether'); // Approve 10,000 USDT
      
      console.log('Approving USDT:', {
        from: account,
        spender: NFT_CONTRACT_ADDRESS,
        amount: maxApproval
      });
      
      const tx = await usdtContract.methods
        .approve(NFT_CONTRACT_ADDRESS, maxApproval)
        .send({ from: account });

      console.log('Approval transaction:', tx);
      console.log('Transaction hash:', tx.transactionHash);
      
      // Wait for transaction to be mined
      let receipt;
      if (tx.blockNumber) {
        receipt = tx;
        console.log('Transaction confirmed in block:', tx.blockNumber);
      } else {
        // Wait for receipt
        console.log('Waiting for transaction receipt...');
        receipt = await web3.eth.getTransactionReceipt(tx.transactionHash);
        
        // Poll for receipt if not immediately available
        let attempts = 0;
        while (!receipt && attempts < 30) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          receipt = await web3.eth.getTransactionReceipt(tx.transactionHash);
          attempts++;
        }
        
        if (!receipt) {
          throw new Error('Transaction receipt not found after waiting');
        }
        console.log('Transaction confirmed in block:', receipt.blockNumber);
      }

      toast.success(t('usdtApproved'));
      
      // Wait a bit for blockchain state to update
      await new Promise((resolve) => setTimeout(resolve, 2000));
      
      // Verify approval with retry logic
      let verified = false;
      for (let i = 0; i < 5; i++) {
        await checkUSDTApproval();
        
        // Check if approval is now true
        const usdtContractCheck = new web3.eth.Contract(USDT_ABI, USDT_ADDRESS);
        const price = web3.utils.toWei(mintPrice.toString(), 'ether');
        const allowance = await usdtContractCheck.methods.allowance(account, NFT_CONTRACT_ADDRESS).call();
        // Web3.js v4 doesn't have toBN, use BigInt instead
        const allowanceBigInt = BigInt(allowance.toString());
        const priceBigInt = BigInt(price.toString());
        const isApproved = allowanceBigInt >= priceBigInt;
        
        console.log(`Approval check attempt ${i + 1}:`, {
          allowance: allowance.toString(),
          price: price.toString(),
          isApproved
        });
        
        if (isApproved) {
          setUsdtApproved(true);
          verified = true;
          console.log('USDT approval verified successfully!');
          break;
        }
        
        // Wait before retry
        if (i < 4) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }
      
      if (!verified) {
        console.warn('Approval not verified after retries, but transaction was successful');
        // Still set to true if transaction succeeded
        setUsdtApproved(true);
      }
      
    } catch (error) {
      console.error('Approval error:', error);
      setUsdtApproved(false);
      if (error.code === 4001) {
        // User rejected the transaction
        toast.info(t('transactionRejected'));
      } else if (error.message?.includes('User denied')) {
        toast.info(t('transactionRejected'));
      } else {
        // Other errors
        const errorMessage = getErrorMessage(error);
        toast.error(`${t('approvalFailed')}: ${errorMessage}`);
        console.error('Full error details:', error);
      }
    } finally {
      setApproving(false);
    }
  };

  // Main minting function
  const handleMint = async () => {
    if (!isConnected || !account) {
      toast.error(t('pleaseConnectWallet'));
      return;
    }

    // Note: Authentication is NOT required for minting
    // Users can mint directly from the smart contract without backend authentication
    // Authentication is only needed for dashboard features

    if (!NFT_CONTRACT_ADDRESS) {
      toast.error(t('contractNotDeployed'));
      return;
    }
    const chainId = Number(await web3.eth.getChainId());
    if (chainId !== BSC_CHAIN_ID) {
      toast.error('Please switch wallet network to BNB Smart Chain.');
      return;
    }

    if (alreadyOwnsNft) {
      toast.info(t('alreadyMinted'));
      return;
    }

    // Re-check approval on-demand (with read-only fallback) before blocking mint.
    if (!usdtApproved) {
      const approvedNow = await checkUSDTApproval();
      if (!approvedNow) {
        toast.info(t('approveUSDTFirst'));
        return;
      }
    }

    setMinting(true);
    try {
      const nftContract = new web3.eth.Contract(NFT_ABI, NFT_CONTRACT_ADDRESS);
      const roWeb3 = await getReadOnlyWeb3();
      const nftReadContract = roWeb3 ? new roWeb3.eth.Contract(NFT_ABI, NFT_CONTRACT_ADDRESS) : null;
      
      // Pre-flight checks
      console.log('Pre-flight checks before minting...');
      
      // Determine active mint mode from read-only BSC RPC (stable)
      const safeRead = async (fn) => {
        try {
          return await fn();
        } catch (_) {
          return null;
        }
      };
      const [presaleActiveNow, publicSaleActiveNow] = await Promise.all([
        nftReadContract ? safeRead(() => nftReadContract.methods.presaleActive().call()) : Promise.resolve(null),
        nftReadContract ? safeRead(() => nftReadContract.methods.publicSaleActive().call()) : Promise.resolve(null)
      ]);
      console.log('Sale state:', { presaleActiveNow, publicSaleActiveNow });

      const saleStateKnown = presaleActiveNow !== null && publicSaleActiveNow !== null;
      if (saleStateKnown && !presaleActiveNow && !publicSaleActiveNow) {
        toast.error('Mint is not active yet. Please wait for activation.');
        setMinting(false);
        return;
      }
      
      // Check USDT balance from read-only BSC RPC
      const usdtReadContract = roWeb3 ? new roWeb3.eth.Contract(USDT_ABI, USDT_ADDRESS) : null;
      const balance = usdtReadContract
        ? await usdtReadContract.methods.balanceOf(account).call()
        : await new web3.eth.Contract(USDT_ABI, USDT_ADDRESS).methods.balanceOf(account).call();
      const price = (roWeb3 || web3).utils.toWei(mintPrice.toString(), 'ether');
      const balanceBigInt = BigInt(balance.toString());
      const priceBigInt = BigInt(price.toString());
      
      console.log('USDT Balance:', balance.toString());
      console.log('Price needed:', price.toString());
      
      if (balanceBigInt < priceBigInt) {
        toast.error(`Insufficient USDT balance. You need ${mintPrice} USDT to mint.`);
        setMinting(false);
        return;
      }
      
      // Check BNB balance for gas
      const bnbBalance = await web3.eth.getBalance(account);
      console.log('BNB Balance:', bnbBalance.toString());
      
      if (BigInt(bnbBalance) < BigInt(web3.utils.toWei('0.001', 'ether'))) {
        toast.error('Insufficient BNB for gas fees. You need at least 0.001 BNB.');
        setMinting(false);
        return;
      }
      
      // Determine which mint function to call (prefer public if explicitly active)
      const mintMethod = publicSaleActiveNow === true
        ? nftContract.methods.publicMint()
        : nftContract.methods.presaleMint();

      console.log('Estimating gas...');
      // Estimate gas
      let gasEstimate;
      try {
        // Dry-run first to surface revert reason in wallets/providers that hide it in estimate errors
        await mintMethod.call({ from: account });
        gasEstimate = await mintMethod.estimateGas({ from: account });
        console.log('Gas estimate:', gasEstimate.toString());
      } catch (gasError) {
        console.error('Gas estimation failed:', gasError);
        // Extract revert reason if available
        const reason = getErrorMessage(gasError);
        if (reason.includes('Presale is not active')) {
          toast.error('Presale is not active. Please wait for activation.');
        } else if (reason.includes('Presale supply exceeded')) {
          toast.error('Presale sold out!');
        } else if (reason.toLowerCase().includes('already minted') || reason.toLowerCase().includes('already own')) {
          toast.error('This wallet already minted an NFT.');
        } else if (reason.toLowerCase().includes('insufficient allowance') || reason.toLowerCase().includes('allowance')) {
          toast.error('USDT allowance is too low. Please click Approve USDT again.');
        } else {
          toast.error(`Transaction will fail: ${reason}`);
        }
        setMinting(false);
        return;
      }
      
      console.log('Sending mint transaction...');
      // Execute mint
      const tx = await mintMethod.send({
        from: account,
        gas: Math.floor(Number(gasEstimate) * 1.2) // Add 20% buffer
      });

      console.log('Mint successful!', tx);
      toast.success(t('nftMintedSuccessfully'));
      
      // Refresh stats
      const presaleMintedCount = await nftContract.methods.presaleMinted().call();
      setPresaleMinted(parseInt(presaleMintedCount));
      
      // Reset approval status (user might need to mint again)
      await checkUSDTApproval();
      
    } catch (error) {
      console.error('Minting error:', error);
      
      // Extract detailed error message
      const errorMessage = getErrorMessage(error);
      
      if (error.code === 4001) {
        toast.error(t('transactionRejected'));
      } else if (errorMessage.includes('Presale is not active')) {
        toast.error('Presale is not active yet. Contact the project owner.');
      } else if (errorMessage.includes('Presale supply exceeded')) {
        toast.error('Presale is sold out!');
      } else if (errorMessage.includes('insufficient funds')) {
        toast.error('Insufficient BNB for gas fees.');
      } else if (errorMessage.includes('ERC20: transfer amount exceeds balance')) {
        toast.error('Insufficient USDT balance.');
      } else if (errorMessage.toLowerCase().includes('already minted') || errorMessage.toLowerCase().includes('already own')) {
        toast.error('This wallet already minted an NFT.');
      } else if (errorMessage.toLowerCase().includes('insufficient allowance') || errorMessage.toLowerCase().includes('allowance')) {
        toast.error('USDT allowance is too low. Please click Approve USDT again.');
      } else {
        // Show more detailed error
        const shortError = errorMessage.length > 150 
          ? errorMessage.substring(0, 150) + '...' 
          : errorMessage;
        toast.error(`${t('mintingFailed')}: ${shortError}`);
      }
    } finally {
      setMinting(false);
    }
  };

  return (
    <section className="minting-section">
      <div className="minting-container">
        <h2 className="minting-title">{t('presaleMinting')}</h2>
        <p className="minting-description">
          {t('mintDescription')}
        </p>

        <div className="minting-stats">
          <div className="stat-item">
            <span className="stat-label">{t('minted')}</span>
            <span className="stat-value">{presaleMinted} / {maxPresale}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('price')}</span>
            <span className="stat-value">{mintPrice} USDT</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('remaining')}</span>
            <span className="stat-value">{maxPresale - presaleMinted}</span>
          </div>
        </div>

        <div className="minting-progress">
          <div
            className="progress-bar"
            style={{ width: `${maxPresale > 0 ? (presaleMinted / maxPresale) * 100 : 0}%` }}
          />
        </div>

        {!alreadyOwnsNft && !usdtApproved && isConnected && account && !approving && (
          <button
            className="mint-button approve-button"
            onClick={handleApproveUSDT}
            disabled={approving || !isConnected || !account || !NFT_CONTRACT_ADDRESS}
            style={{ marginBottom: '0.75rem' }}
          >
            {approving ? t('approvingUSDT') : !NFT_CONTRACT_ADDRESS ? t('contractNotDeployed') : t('approveUSDT')}
          </button>
        )}
        {
          <button
            className="mint-button"
            onClick={handleMint}
            disabled={minting || approving || !isConnected || alreadyOwnsNft || presaleMinted >= maxPresale}
          >
            {minting
              ? t('minting')
              : approving
              ? t('approvingUSDT')
              : !isConnected
              ? t('connectWalletToMint')
              : alreadyOwnsNft
              ? t('alreadyMinted')
              : presaleMinted >= maxPresale
              ? t('soldOut')
              : `${t('mintNFT')} (${mintPrice} USDT)`}
          </button>
        }

        {presaleMinted >= maxPresale && (
          <p className="minting-note">
            {t('presaleComplete')}
          </p>
        )}
      </div>
    </section>
  );
};

export default MintingSection;
